import React, { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LogOut,
  Mail,
  Plus,
  Send,
  Settings,
  Truck,
  Users,
  X,
} from "lucide-react";

import AuthContext from "../../contexts/AuthCtx";
import { api } from "../../api/api";
import { DELIVERY_STATUS } from "../../utils/letterSend";

function PendingBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brandPurple px-1.5 font-josefin text-[11px] font-bold leading-none text-white">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function Sidebar({
  onNewLetter,
  isMobileOpen = false,
  onNavigate,
  onCloseMobile,
}) {
  const AuthCtx = useContext(AuthContext);
  const [pendingExpedition, setPendingExpedition] = useState(0);
  const [pendingLifetime, setPendingLifetime] = useState(0);

  useEffect(() => {
    if (!AuthCtx.isAdmin || !AuthCtx.userToken) {
      setPendingExpedition(0);
      setPendingLifetime(0);
      return;
    }

    let cancelled = false;
    const headers = { Authorization: `Bearer ${AuthCtx.userToken}` };

    const countPending = (recipients = []) =>
      recipients.filter((r) => r.deliveryStatus === DELIVERY_STATUS.PENDING)
        .length;

    Promise.all([
      api.get("admin/getLetters", { headers }).catch(() => null),
      api.get("admin/getLifetimeLetters", { headers }).catch(() => null),
    ]).then(([expeditionRes, lifetimeRes]) => {
      if (cancelled) return;

      const deathLetters = expeditionRes?.data?.lettersWithDiedUsers || [];
      const expeditionCount = deathLetters.reduce(
        (sum, letter) => sum + countPending(letter.recipients),
        0
      );

      const lifetimeLetters = lifetimeRes?.data?.letters || [];
      const lifetimeCount = lifetimeLetters.reduce(
        (sum, letter) => sum + countPending(letter.recipients),
        0
      );

      setPendingExpedition(expeditionCount);
      setPendingLifetime(lifetimeCount);
    });

    return () => {
      cancelled = true;
    };
  }, [AuthCtx.isAdmin, AuthCtx.userToken]);

  const navClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 font-josefin text-base transition duration-200 ${
      isActive
        ? "bg-white text-secondaryText shadow-sm"
        : "text-textMuted hover:bg-white/70 hover:text-secondaryText"
    }`;

  const handleLogout = () => {
    AuthCtx.logout();
    window.location.href = "/login";
  };

  const handleNewLetter = () => {
    onNewLetter?.();
    onNavigate?.();
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 flex h-[100dvh] w-[min(300px,88vw)] shrink-0 -translate-x-full flex-col bg-dashboardSidebar px-5 py-5 shadow-xl transition-transform duration-300 lg:static lg:inset-auto lg:z-auto lg:h-full lg:w-[240px] lg:translate-x-0 lg:border-r lg:border-greyLight/20 lg:px-4 lg:py-6 lg:shadow-none xl:w-[260px] ${
        isMobileOpen ? "translate-x-0" : ""
      }`}
    >
      <div className="mb-6 flex items-center justify-between lg:hidden">
        <span className="font-logo text-xl text-black">ByeByeRegrets</span>
        <button
          type="button"
          onClick={onCloseMobile}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-secondaryText transition hover:bg-white/70"
          aria-label="Fermer le menu"
        >
          <X size={20} />
        </button>
      </div>

      <button
        type="button"
        onClick={handleNewLetter}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg bg-brandPurple py-3.5 font-josefin text-base text-white transition duration-200 hover:bg-primaryText"
      >
        <Plus size={18} />
        Nouvelle lettre
      </button>

      <nav className="flex flex-col gap-1">
        <NavLink to="/dashboard" end className={navClass} onClick={onNavigate}>
          <Mail size={18} />
          Mes lettres
        </NavLink>
      </nav>

      {AuthCtx.isAdmin && (
        <nav className="mt-6 flex flex-col gap-1 border-t border-greyLight/40 pt-4">
          <p className="mb-2 px-4 font-josefin text-xs font-bold uppercase tracking-wider text-textMuted">
            Administration
          </p>
          <NavLink to="/admin/all-users" className={navClass} onClick={onNavigate}>
            <Users size={18} />
            Utilisateurs
          </NavLink>
          <NavLink to="/admin/expedition" className={navClass} onClick={onNavigate}>
            <Truck size={18} />
            <span className="flex-1">Expédition (décès)</span>
            <PendingBadge count={pendingExpedition} />
          </NavLink>
          <NavLink to="/admin/lifetime-sends" className={navClass} onClick={onNavigate}>
            <Send size={18} />
            <span className="flex-1">Envois immédiats</span>
            <PendingBadge count={pendingLifetime} />
          </NavLink>
        </nav>
      )}

      <div className="mt-auto flex flex-col gap-1 border-t border-greyLight/40 pt-4">
        <NavLink to="/settings" className={navClass} onClick={onNavigate}>
          <Settings size={18} />
          Réglages
        </NavLink>
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 rounded-xl px-4 py-3 font-josefin text-base text-textMuted transition duration-200 hover:bg-white/70 hover:text-red"
        >
          <LogOut size={18} />
          Se déconnecter
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
